import Tokenizer, {
  UnknownCharacterError,
  ExpectedCharacterError,
} from './tokenizer'

const numberReviver = (val, multiplier) => String(parseFloat(val) * multiplier)

const numberModifiersRevivers = {
  k: val => numberReviver(val, 1000),
  m: val => numberReviver(val, 1000000),
}

const VALID_NUMBER_MODIFIERS = Object.keys(numberModifiersRevivers)

const isValidNumberModifier = ch => VALID_NUMBER_MODIFIERS.includes(ch)

// NOTE: I am intentionally not supporting floats here, because I do not need
// floats for what I am doing. But if you did want floats, change this boondog, and
// change lexNumber to account for the periods.
const NUMBER_PATTERN = /^\d+$/
const isNumber = val => NUMBER_PATTERN.test(val)

class RangeTokenizer extends Tokenizer {
  tokenize() {
    const character = this.next()

    if (character === '*') {
      return this.lexWildcard()
    } else if (character === '>') {
      return this.lexGreaterExpression()
    } else if (character === '<') {
      return this.lexLessExpression()
    } else if (isNumber(character)) {
      return this.lexNumberExpression()
    } else {
      this.errors.push(new UnknownCharacterError(character))
      this.terminate()
    }
  }

  lexWildcard() {
    return { type: 'any', value: null }
  }

  lexGreaterExpression() {
    this.next()
    let type = 'greaterThan'

    if (this.current() === '=') {
      type = 'greaterThanInclusive'
      this.next()
    }

    this.chompSpace()

    if (!isNumber(this.current())) {
      return this.errors.push(new UnknownCharacterError(this.current()))
    }

    const value = this.lexNumber()

    return { type, value }
  }

  lexLessExpression() {
    this.next()
    let type = 'lessThan'

    if (this.current() === '=') {
      type = 'lessThanInclusive'
      this.next()
    }
    this.chompSpace()

    if (!isNumber(this.current())) {
      return this.errors.push(new UnknownCharacterError(this.current()))
    }

    const value = this.lexNumber()

    return { type, value }
  }

  lexNumber() {
    let acc = this.current()
    let decimalSeen = false
    let cur = this.peek()

    while (
      isNumber(cur) ||
      isValidNumberModifier(cur) ||
      (cur === '.' && !decimalSeen)
    ) {
      if (cur === '.') {
        decimalSeen = true
      }

      this.next()

      // if we hit a modifier, we can't possibly have another digit representation so we can break
      if (isValidNumberModifier(cur)) {
        acc = numberModifiersRevivers[cur](acc)
        break
      }

      acc = `${acc}${this.current()}`
      cur = this.peek()
    }

    return acc
  }

  lexNumberExpression() {
    const firstNumber = this.lexNumber()
    let secondNumber

    this.chompSpace()

    const nextChar = this.next()

    if (nextChar === null) {
      return { type: 'singleValue', value: firstNumber }
    } else if (nextChar === '-') {
      this.chompSpace()

      if (isNumber(this.peek())) {
        this.next()
        secondNumber = this.lexNumber()
        const [low, high] = [firstNumber, secondNumber].sort((a, b) => a - b)

        return {
          type: 'between',
          value: { low, high },
        }
      }
    }

    this.errors.push(new ExpectedCharacterError('-', nextChar))
  }
}

class Range {
  static types = {
    unknown: 'unknown',
    // *
    any: 'any',
    // 5
    singleValue: 'single_value',
    // >5
    greaterThan: 'greater_than',
    // >=5
    greaterThanInclusive: 'greater_than_inclusive',
    // <5
    lessThan: 'less_than',
    // <=5
    lessThanInclusive: 'less_than_inclusive',
    // 5-7
    between: 'between',
  }
}

class NumberRange extends Range {
  static from(str) {
    return new NumberRange(str)
  }

  static isValidString(str) {
    return new NumberRange(str).valid()
  }

  constructor(raw) {
    super(raw)

    this.raw = raw
    this.tokenizer = new RangeTokenizer(raw)
  }

  valid() {
    return !!this.tokenizer.valid
  }

  get type() {
    return NumberRange.types[this.tokenizer.expression.type]
  }

  get value() {
    return this.tokenizer.expression.value
  }

  // Ties a range to a apecific field
  toSearchCondition(field) {
    const range = {}

    // Simple case
    if (this.type === NumberRange.types.singleValue) {
      return {
        equals: {
          fields: field,
          values: [this.value],
        },
      }
    }

    switch (this.type) {
      case Range.types.lessThan:
        range.lt = this.value
        break
      case Range.types.lessThanInclusive:
        range.lte = this.value
        break
      case Range.types.greaterThan:
        range.gt = this.value
        break
      case Range.types.greaterThanInclusive:
        range.gte = this.value
        break
      case Range.types.between:
        range.gte = this.value.low
        range.lte = this.value.high
    }

    return {
      range: {
        field,
        ...range,
      },
    }
  }
}

export { NumberRange }
